// /////////////////////////////////////////////////////////////////////
//
//
// (C) Copyright 2021 Autodesk, Inc. All rights reserved.
//
//                     ****  CONFIDENTIAL MATERIAL  ****
//
// The information contained herein is confidential, proprietary to
// Autodesk, Inc., and considered a trade secret.  Use of this information
// by anyone other than authorized employees of Autodesk, Inc. is granted
// only under a written nondisclosure agreement, expressly prescribing the
// the scope and manner of such use.
//
// /////////////////////////////////////////////////////////////////////

import {
  CreateJobRequest,
  EditJobRequest,
  JobRun
} from "../clients/Classes";
import {ClientProvider} from "../clients/ClientProvider";
import {Task} from "../dataModel/Task";
import {TaskTranslator} from "../dataModel/Translators/TaskTranslator";
import {ServiceBase} from "./ServiceBase";
import {IV2Client} from "../clients/V2Client";
import {CancellationToken} from "../dataModel/CancellationToken";
import {IClient} from "../clients/Client";
import {JobPaginatedResult} from "../clients/V2Classes";

export class TaskService extends ServiceBase {
  private _client: IClient = ClientProvider.Client;
  private _v2Client: IV2Client = ClientProvider.V2Client;

  GetTasks(
    paginationToken?: string | null | undefined,
    limit?: number | undefined,
    loadFilesAndFolders?: boolean,
    loadItems?: boolean
  ): Promise<JobPaginatedResult> {
    return this.TryPromiseWithCatch(() =>
      this.GetResultsWithItemOrToEnd(t => this._v2Client.listJobs(t, limit, loadFilesAndFolders, loadItems), paginationToken)
        .then(r => new JobPaginatedResult(r))
    );
  }

  GetRemainingTasks(
    paginationToken?: string | null | undefined,
    limit?: number | undefined,
    loadFilesAndFolders?: boolean,
    loadItems?: boolean,
    cancelToken?: CancellationToken | undefined
  ): Promise<JobPaginatedResult> {
    return this.TryPromiseWithCatch(() =>
      this.GetRemainingDataFromPaginatedEndpoint(
        token => this._v2Client.listJobs(token, limit, loadFilesAndFolders, loadItems), paginationToken, cancelToken)
        .then(jobs => new JobPaginatedResult(jobs))
    );
  }

  CreateTask(command: CreateJobRequest): Promise<Task> {
    return this.TryPromiseWithCatch(() =>
      this._client.createJob(command)
        .then(job => TaskTranslator.GetTask(job))
    );
  }

  GetTask(id: string): Promise<Task> {
    return this.TryPromiseWithCatch(() =>
      this._client.getJob(id)
        .then(job => TaskTranslator.GetTask(job))
    );
  }

  RunTask(id: string): Promise<JobRun> {
    return this.TryPromiseWithCatch(() =>
      this._client.startJob(id)
    );
  }

  UpdateTask(task: Task, update: EditJobRequest): Promise<Task> {
    return this.TryPromiseWithCatch(() =>
      this._client.editJob(task.Id!, update)
        .then(job => TaskTranslator.GetTask(job))
    );
  }

  DeleteTask(task: Task): Promise<boolean> {
    return this.TryPromiseWithCatch(() =>
      this._client.deleteJob(task.Id!).then(() => true)
    );
  }

  ToggleTaskPause(task: Task): Promise<boolean> {
    const command: EditJobRequest = new EditJobRequest({
      pauseJob: !task.IsPaused
    });

    return this.TryPromiseWithCatch(() =>
      this._client.editJob(task.Id!, command)
        .then(_job => {
          task.IsPaused = _job.schedule != null && _job.schedule.isPaused!;
          task.RawStatus = _job.status;
          return true;
        })
    );
  }
}